<template>
  <ViewContainer
    :title="`${$t('common.challenge_other')} in ${organization.name}`"
    :breadcrumb="breadcrumbs"
  >
    <v-card>
      <v-card-title>
        <v-row class="mt-2">
          <v-col cols="5">
            <SearchInput
              class="mr-4"
              :value="searchTerms"
              @change="handleSearch"
            />
          </v-col>
          <v-col></v-col>
          <v-col cols="1">
            <OrganizationMenu :organizationId="organization._id" />
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        id="data-challenges-table"
        class="elevation-1 mt-4"
        data-private
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalRecordsInData"
        :loading="isLoading"
        :footer-props="footerOptions"
      >
        <template v-slot:item.name="{ item }">
          <router-link
            class="cursor-pointer text-decoration-none"
            :to="`/challenge/${organizationId}/${item.id}`"
          >
            {{ item.name }}
          </router-link>
        </template>
      </v-data-table>
    </v-card>
  </ViewContainer>
</template>

<script>
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import OrganizationMenu from './OrganizationMenu.vue'

import mixin from '@/mixins/mixin.vue'

import _ from 'lodash'
import { mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'

export default {
  name: 'Challenges',

  mixins: [mixin],

  components: {
    ViewContainer,
    SearchInput,
    OrganizationMenu,
  },

  data() {
    return {
      breadcrumbs: [],
      organizationId: this.getURLParam('organizationId'),
      totalRecordsInData: 0,
      currentPage: 1,
      DEFAULT_SIZE: 20,
      challenges: [],
      isLoading: true,
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: this.$t('common.site_other'),
          sortable: false,
          value: 'sites',
        },
        {
          text: this.$t('common.created_by'),
          sortable: false,
          value: 'createdBy',
        },
        {
          text: this.$t('common.starts_at'),
          sortable: true,
          value: 'startAt',
        },
        {
          text: this.$t('common.ends_at'),
          sortable: true,
          value: 'endAt',
        },
      ],
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      organization: {},
      searchTerms: '',
    }
  },

  async mounted() {
    this.organization = await this.getOrganization()
    this.breadcrumbs = [
      {
        href: '/admin/organizations',
        name: this.$t('common.organization_other'),
      },
      {
        href: `/organization-settings/${this.organizationId}`,
        name: this.organization.name,
      },
      { name: this.$t('common.challenge_other') },
    ]
  },

  computed: {
    items() {
      return this.challenges.map(challenge => {
        return {
          id: utils.getEntityId(challenge),
          name: challenge?.name,
          sites: this.getText(challenge?.site),
          createdBy: this.getFullName(challenge?.createdBy),
          startAt: utils.dates.humanDate(challenge?.startAt),
          endAt: utils.dates.humanDate(challenge?.endAt),
        }
      })
    },

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: this.DEFAULT_SIZE,
          multiSort: false,
          mustSort: false,
          page: 1,
        }
      },
      set(event) {
        this.getChallenges({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  methods: {
    ...mapActions({
      get: 'getChallenges',
      fetchOrganization: 'getOrganization',
    }),

    async getChallenges(options) {
      this.isLoading = true
      const size = options.itemsPerPage || this.DEFAULT_SIZE
      const isSorting = options?.sortBy
      const sort = { by: '', order: '' }
      if (isSorting) {
        sort.by = options.sortBy
        sort.order = options.sortDesc ? 'desc' : 'asc'
      } else {
        sort.by = 'createdAt'
        sort.order = 'desc'
      }
      try {
        const response = await this.get({
          organizationId: this.organizationId,
          limit: size,
          terms: this.searchTerms ?? null,
          from: options.page * size - size,
          sort,
          projections: ['name', 'startAt', 'endAt', 'createdBy', 'site'],
          expansions: ['createdBy', 'site'],
        })

        this.challenges = response?.results ?? []
        this.totalRecordsInData = response?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async getOrganization() {
      this.isLoading = true
      try {
        return this.fetchOrganization({
          organizationId: this.organizationId,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.getChallenges(this.options)
    },

    getText(object) {
      const names = object?.map(item => item.name)
      return _.join(names, ', ')
    },
  },
}
</script>
